import { FC, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Tag, TagVariant } from '../../../components/tag/index';
import {
  EventLifecycleStatus,
  EventUnionWithPermissions,
  isPendingLifecycleStatus,
} from '../../../types/events.types';

type EventLifecycleWrapperProps = PropsWithChildren<{
  event: EventUnionWithPermissions;
}>;

// Wraps an event row while its mutation is in the queue: pending create/edit/delete all render
// the skeleton placeholder, dimmed and non-interactive, so a queued edit or delete reads the
// same as a queued create (the floating PendingOperationsPill narrates the in-flight work);
// a failed apply keeps the row interactive (re-editing retries) and wears a red badge on a
// gradient panel that masks the row's actions — the panel fades out to the left instead of
// ending in a hard edge, so a badge crossing the row's midpoint is never sliced mid-chip
// (QA: truncated "Pre-…" chip).
export const EventLifecycleWrapper: FC<EventLifecycleWrapperProps> = ({ event, children }) => {
  const status = event.lifecycleStatus;
  const isPendingMutation = isPendingLifecycleStatus(status);
  const isFailed = status === EventLifecycleStatus.FAILED;

  if (!isPendingMutation && !isFailed) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      <div
        className={twMerge(
          isPendingMutation && 'pointer-events-none opacity-50',
          isFailed && 'rounded-md ring-1 ring-fireside-200',
        )}
      >
        {children}
      </div>
      {isFailed && (
        <>
          <div className="pointer-events-none absolute inset-0 rounded-md bg-fireside-500/10" />
          <div className="absolute inset-y-0 right-0 flex w-1/2 items-center justify-end rounded-r-md bg-gradient-to-l from-white from-45% to-transparent pr-10">
            <div className="absolute inset-0 rounded-r-md bg-gradient-to-l from-fireside-500/10 from-45% to-transparent" />
            <Tag
              className="pointer-events-none relative bg-fireside-500/10 text-fireside-600"
              title={<AppFormattedMessage id="EVENT_STATUS_FAILED" />}
              variant={TagVariant.RED}
            />
          </div>
        </>
      )}
    </div>
  );
};
